import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/lib/api";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";

/**
 * Ô chọn nhà cung cấp / sản phẩm / đơn mua cho khiếu nại nhà cung cấp.
 *
 * Đọc qua `/supplier-claims/options/*` chứ không qua `/suppliers` hay `/products`: nhân viên chất
 * lượng lập khiếu nại có thể không được tick menu Nhà cung cấp, và không nên cần.
 */
export function ClaimOptionPicker({
  kind,
  value,
  selectedLabel,
  label,
  supplierId = "",
  disabled = false,
  allowEmpty = false,
  onChange,
}: {
  kind: "suppliers" | "products" | "purchaseOrders";
  value: string;
  selectedLabel?: string;
  label: string;
  supplierId?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  onChange: (id: string, label: string) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const enabled = !disabled && (kind !== "purchaseOrders" || !!supplierId);
  const query = useQuery({
    queryKey: ["supplier-claim-options", kind, q, supplierId],
    enabled,
    queryFn: ({ signal }) => apiFetch<{ data: { id: string; label: string }[] }>(`/supplier-claims/options/${kind}`, { query: { q, supplierId }, signal }),
  });
  const id = `supplier-claim-${kind}`;
  return (
    <div className="min-w-0">
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      {!disabled && <Input aria-label={`${t("common.search")} · ${label}`} placeholder={t("common.typeToSearch")} className="mb-2 min-h-11" value={q} onChange={(e) => setQ(e.target.value)} />}
      <Select
        id={id}
        value={value}
        containerClassName="w-full min-h-11"
        disabled={disabled || (enabled && query.isLoading)}
        onChange={(e) => onChange(e.target.value, query.data?.data.find((r) => r.id === e.target.value)?.label ?? "")}
      >
        <option value="">{allowEmpty ? t("supplierClaims.none") : t("warehouse.select")}</option>
        {value && !query.data?.data.some((r) => r.id === value) && <option value={value}>{selectedLabel ?? value}</option>}
        {query.data?.data.map((r) => (
          <option key={r.id} value={r.id}>
            {r.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
